/**
 * Client-side record of which files already landed.
 *
 * This is the only resume mechanism available: the SAS has no list or read
 * permission, so the app can never ask the container what it already holds.
 * A 12 GB session runs for hours and will be interrupted, so this store is
 * what makes a reload cheap instead of catastrophic.
 */

const DB_NAME = "afad-veri-toplama";
const DB_VERSION = 1;
const STORE = "completed";
const SESSION_KEY = "afad.session";

/** Identity of a picked file: name + size + mtime is stable across re-picks of the same folder. */
export function fileKey(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  return `${rel}|${file.size}|${file.lastModified}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // keyPath-less store: key is `${sessionId}::${fileKey}`.
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function compositeKey(sessionId: string, key: string): string {
  return `${sessionId}::${key}`;
}

export async function markCompleted(sessionId: string, key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(1, compositeKey(sessionId, key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadCompleted(sessionId: string): Promise<Set<string>> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out = new Set<string>();
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).openKeyCursor();
    const wanted = `${sessionId}::`;
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(out);
        return;
      }
      const k = String(cursor.key);
      if (k.startsWith(wanted)) out.add(k.slice(wanted.length));
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

export async function clearSession(sessionId: string): Promise<void> {
  const db = await openDb();
  const keys = await loadCompleted(sessionId);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const k of keys) store.delete(compositeKey(sessionId, k));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Form values are kept alongside the session id so a reload can rebuild the same blob paths. */
export interface StoredSession {
  sessionId: string;
  form: unknown;
  savedAt: string;
}

export function saveSession(sessionId: string, form: unknown): void {
  try {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ sessionId, form, savedAt: new Date().toISOString() })
    );
  } catch {
    /* private browsing / quota - resume simply won't be offered */
  }
}

export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

export function forgetSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}
